<script setup lang="ts">
import { cn } from '@/lib/utils'
import { Cross2Icon } from '@radix-icons/vue'
import {
    DialogClose,
    DialogContent,
    type DialogContentEmits,
    type DialogContentProps,
    DialogOverlay,
    DialogPortal,
    useEmitAsProps,
} from 'reka-ui'

const props = withDefaults(
    defineProps<
        DialogContentProps & {
            class?: string
            withCloseButton?: boolean
            closeOnClickOutside?: boolean
        }
    >(),
    {
        withCloseButton: true,
        class: undefined,
        closeOnClickOutside: true,
    }
)
const emits = defineEmits<DialogContentEmits>()

const emitsAsProps = useEmitAsProps(emits)
</script>

<template>
    <DialogPortal>
        <DialogOverlay
            class="dialog-overlay fixed inset-0 z-50 bg-black opacity-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogContent
            :class="
                cn(
                    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
                    props.class
                )
            "
            v-bind="{ ...props, ...emitsAsProps }"
            @interact-outside="
                (event) => {
                    if (!closeOnClickOutside) {
                        return event.preventDefault()
                    }

                    const target = event.target as HTMLElement
                    if (target?.closest('[data-sonner-toaster]')) return event.preventDefault()
                }
            "
        >
            <slot />

            <DialogClose
                v-if="props.withCloseButton"
                class="absolute top-4 right-4 p-1 transition-colors rounded-full hover:bg-secondary"
            >
                <Cross2Icon class="w-4 h-4" />
                <span class="sr-only">Close</span>
            </DialogClose>
        </DialogContent>
    </DialogPortal>
</template>
